import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Slide,
  useTheme,
  useMediaQuery,
  Button,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Delete as DeleteIcon,
  Add,
  PhotoCamera,
  Collections,
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { useTranslation } from 'react-i18next';
import { Photos, Project } from '../types';
import PhotoSelectorGallery from './PhotoSelectorGallery';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { defaultRetryPolicy } from '../utils/retryPolicy';
import { analyzeImageQuality, QualityReport } from '../functions/qualityCheck';
import { captureException } from '../utils/captureException';

interface PhotoGalleryProps {
  open: boolean;
  onClose: () => void;
  initialPhotoType?: string;
  photos: Photos[];
  isMobile: boolean;
  multiple?: boolean;
  onPhotoDeleted?: () => void;
  enableAddPhoto?: boolean;
  projectCode?: string;
  zone?: string;
  type?: string;
  mandatory?: boolean;
  isOption?: boolean;
  title?: string;
  option?: string;
  project?: Project;
  grouped?: boolean;
}

interface GalleryImage {
  id: string;
  zone: string;
  url: string;
  thumbnail_url?: string;
  status: string;
  type: 'photo' | 'document';
  bboxes?: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  open,
  onClose,
  initialPhotoType,
  photos,
  isMobile,
  multiple = false,
  mandatory = false,
  onPhotoDeleted,
  enableAddPhoto = false,
  projectCode,
  isOption = false,
  title,
  option,
  project,
  grouped = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [images, setImages] = useState<GalleryImage[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [touchStartTime, setTouchStartTime] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [transition, setTransition] = useState(true);
  const [showBoundingBoxes, setShowBoundingBoxes] = useState(true);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [imageAspectRatios, setImageAspectRatios] = useState<Map<string, number>>(new Map());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [qualityCheck, setQualityCheck] = useState<{
    open: boolean;
    report: QualityReport | null;
    file: File | null;
  }>({
    open: false,
    report: null,
    file: null,
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<Map<string, HTMLImageElement>>(new Map());
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Prepare images from photos data
  useEffect(() => {
    if (!open) return;
    
    // If photos is null/undefined, don't update images (keep existing ones)
    // This prevents the gallery from closing during refresh
    if (photos === null || photos === undefined) {
      return;
    }

    const galleryImages: GalleryImage[] = [];

    if (Array.isArray(photos)) {
      photos.forEach((photo: Photos) => {
        if (photo.gcs_path) {
          galleryImages.push({
            id: photo.id,
            zone: photo.zone || '',
            url: photo.gcs_path,
            thumbnail_url: photo.gcs_thumbnail_path,
            status: photo.status || 'pending',
            type: 'photo',
            bboxes: photo.bboxes || [],
          });
        }
      });
    }
    
    // Always update images when we have new gallery images
    // However, if the new array is empty but we already have images, preserve them
    // This prevents the gallery from closing during refresh
    if (galleryImages.length > 0 || images === undefined) {
      setImages(galleryImages);
    }
    // If galleryImages is empty but images already exists, keep existing images
    // This handles the case where photos temporarily becomes empty during refresh
    
    // Set initial index based on photo type
    if (initialPhotoType && galleryImages.length > 0) {
      const initialIndex = galleryImages.findIndex(
        img => img.zone.toLowerCase() === initialPhotoType.toLowerCase()
      );
      setCurrentIndex(initialIndex >= 0 ? initialIndex : 0);
    } else if (galleryImages.length > 0) {
      setCurrentIndex(0);
    }
    
    setZoom(1);
    setDragOffset(0);
    setIsDragging(false);
    setTransition(true);
    setTouchStartTime(null);
    setTouchStart(null);
    setTouchEnd(null);
    setLoadedImages(new Set());
    setImageAspectRatios(new Map());
    setDeleteConfirmOpen(false);
    setDeleting(false);
  }, [open, photos, initialPhotoType]);

  // Keyboard navigation (clamped, not circular)
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          goToNext();
          break;
        case '+':
        case '=':
          event.preventDefault();
          setZoom(prev => Math.min(prev + 0.25, 3));
          break;
        case '-':
          event.preventDefault();
          setZoom(prev => Math.max(prev - 0.25, 0.5));
          break;
        case '0':
          event.preventDefault();
          setZoom(1);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, images?.length]);

  // ------- FIX 1: clamp navigation (no wraparound) -------
  const goToPrevious = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
    setZoom(1);
    setDragOffset(0);
    setIsDragging(false);
  };

  const goToNext = () => {
    setCurrentIndex(prev => Math.min((images?.length || 0) - 1, prev + 1));
    setZoom(1);
    setDragOffset(0);
    setIsDragging(false);
  };
  // -------------------------------------------------------

  const handleTouchStart = (e: React.TouchEvent) => {
    if (zoom !== 1) return; // Only allow carousel movement when zoomed out

    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartTime(Date.now());
    setIsDragging(true);
    setDragOffset(0);

    setTransition(false);
    // prevent default only if we're handling the gesture
    e.preventDefault();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || zoom !== 1) return;

    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);

    if (touchStart !== null) {
      const offset = currentTouch - touchStart;
      setDragOffset(offset);
    }

    e.preventDefault();
  };

  const handleTouchEnd = () => {
    if (!isDragging || zoom !== 1) return;

    setIsDragging(false);
    setTransition(true);

    // ------- FIX 3: check explicitly for null, not falsy 0 -------
    if (touchStart === null || touchEnd === null || touchStartTime === null) {
      setTimeout(() => setDragOffset(0), 300);
      return;
    }
    // --------------------------------------------------------------

    const touchDuration = Date.now() - touchStartTime;
    const distance = touchStart - touchEnd;
    const threshold = 80; // Minimum swipe distance
    const minDuration = 100; // Ignore micro-taps

    if (touchDuration < minDuration) {
      setTimeout(() => setDragOffset(0), 300);
      return;
    }

    const isLeftSwipe = distance > threshold;
    const isRightSwipe = distance < -threshold;

    // -------- FIX 1 applied to swipe: clamp at edges --------
    if (isLeftSwipe) {
      if (currentIndex < (images?.length || 0) - 1) {
        goToNext();
      } else {
        // at last slide: bounce back
        setTimeout(() => setDragOffset(0), 150);
      }
    } else if (isRightSwipe) {
      if (currentIndex > 0) {
        goToPrevious();
      } else {
        // at first slide: bounce back
        setTimeout(() => setDragOffset(0), 150);
      }
    } else {
      setTimeout(() => setDragOffset(0), 300);
    }
  };

  const handleImageLoad = (imageId: string, event?: React.SyntheticEvent<HTMLImageElement>) => {
    setLoading(false);
    setLoadedImages(prev => new Set(prev).add(imageId));
    
    // Calculate and store aspect ratio for grouped view
    if (grouped && event?.currentTarget) {
      const img = event.currentTarget;
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      setImageAspectRatios(prev => new Map(prev).set(imageId, aspectRatio));
    }
  };
  const handleImageError = () => {
    setLoading(false);
  };

  const handleDeleteClick = (e: React.MouseEvent, photoId: string) => {
    e.stopPropagation();
    setPhotoToDelete(photoId);
    setDeleteConfirmOpen(true);
    setDeleteError(null);
  };

  const handleAddPhotoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleCapturePhoto = () => {
    handleMenuClose();
    cameraInputRef.current?.click();
  };

  const handleSelectGallery = () => {
    handleMenuClose();
    setSelectorOpen(true);
  };

  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) {
      setUploadError(t('errors.fileSelection.invalidFileType'));
      return;
    }

    setProcessing(true);
    setUploadError(null);
    
    try {
      // First, analyze image quality
      const qualityReport = await analyzeImageQuality(file);
      
      // If quality is bad, prevent API call and show warning
      if (qualityReport.summary === 'bad') {
        setQualityCheck({
          open: true,
          report: qualityReport,
          file: file,
        });
        return;
      }
      
      // If quality is OK/warn or good, proceed with upload
      if (qualityReport.summary === 'ok') {
        setQualityCheck({
          open: true,
          report: qualityReport,
          file: file,
        });
        return; // User can decide to proceed or retake
      }
      
      setProcessing(false);
      // For good quality, proceed directly
      await proceedWithUpload(file);
      
    } catch (err) {
      captureException(err, { tags: { feature: 'photoGallery' }, extra: { action: 'analyzeImageQuality', project_code: projectCode } });
      console.error('Error analyzing image quality:', err);
      setUploadError(t('errors.processing.failedAnalyzeQuality'));
      setProcessing(false);
    }
  };

  const proceedWithUpload = async (file: File) => {
    setUploadError(null);
    
    try {
      if (!user) throw new Error(t('errors.processing.userNotAuthenticated'));
      if (!projectCode) throw new Error(t('errors.processing.projectCodeRequired'));
      if (!option) throw new Error(t('errors.processing.zoneOrTypeRequired'));
      
      const idToken = await user.getIdToken();

      const formData = new FormData();
      formData.append('image', file, file.name);
      formData.append('userId', user.uid);
      formData.append('project_code', projectCode);
      formData.append('optional', option);

      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/add/image`;
      
      const response = await defaultRetryPolicy.executeWithRetry(async () => {
        return await axios.post<{ id: string }>(apiUrl, formData, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
      }, 'Image Upload API call', { 
        apiUrl, 
        userId: user.uid, 
        projectCode: projectCode,
      });

      if (response.data) {
        console.log('Photo uploaded successfully:', response.data);
        // Refresh the gallery by calling onPhotoDeleted callback
        if (onPhotoDeleted) {
          await onPhotoDeleted();
        }
      } else {
        throw new Error(t('errors.processing.noResultId'));
      }
    } catch (err) {
      captureException(err, { tags: { feature: 'photoGallery' }, extra: { action: 'sendImage', project_code: projectCode } });
      console.error('Error sending image:', err);
      let errorMessage = t('errors.processing.failed');
      if (axios.isAxiosError(err) && err.response) {
        errorMessage = `Error: ${err.response.data?.details || err.response.data?.error || err.message}`;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setUploadError(errorMessage);
    } finally {
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const handleQualityCheckClose = () => {
    setQualityCheck({ open: false, report: null, file: null });
    setProcessing(false);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleProceedWithQuality = async () => {
    if (qualityCheck.file) {
      handleQualityCheckClose();
      await proceedWithUpload(qualityCheck.file);
    }
  };

  const handlePhotoSelected = async (selectedPhoto: Photos) => {
    setUploadError(null);
    
    try {
      if (!user) throw new Error(t('errors.processing.userNotAuthenticated'));
      if (!projectCode) throw new Error(t('errors.processing.projectCodeRequired'));
      if (!option) throw new Error(t('errors.processing.zoneOrTypeRequired'));
      
      const idToken = await user.getIdToken();

      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/assign/image`;
      
      const response = await defaultRetryPolicy.executeWithRetry(async () => {
        return await axios.post(apiUrl, {
          foto_id: selectedPhoto.id,
          optional: option,
          project_code: projectCode,
        }, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
      }, 'Photo Reassignment API call', { 
        apiUrl, 
        userId: user.uid, 
        projectCode: projectCode,
        photoId: selectedPhoto.id,
        newZone: option
      });

      if (response.data) {
        console.log('Photo reassigned successfully:', response.data);
        // Refresh the gallery by calling onPhotoDeleted callback
        if (onPhotoDeleted) {
          await onPhotoDeleted();
        }
        setSelectorOpen(false);
      } else {
        throw new Error(t('errors.processing.noResultId'));
      }
    } catch (err) {
      captureException(err, { tags: { feature: 'photoGallery' }, extra: { action: 'reassignPhoto', project_code: projectCode } });
      console.error('Error reassigning photo:', err);
      let errorMessage = t('errors.processing.failed');
      if (axios.isAxiosError(err) && err.response) {
        errorMessage = `Error: ${err.response.data?.details || err.response.data?.error || err.message}`;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setUploadError(errorMessage);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!photoToDelete || !user) {
      setDeleteConfirmOpen(false);
      setPhotoToDelete(null);
      return;
    }

    setDeleting(true);
    setDeleteError(null);

    try {
        if (!isOption) {
          const idToken = await user.getIdToken();
          
          const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/photos/${photoToDelete}`;
          
          await defaultRetryPolicy.executeWithRetry(async () => {
            return await axios.delete(apiUrl, {
              headers: { 'Authorization': `Bearer ${idToken}` },
            });
          }, 'Delete Photo API call', { 
            apiUrl, 
            userId: user.uid, 
            photoId: photoToDelete
          });

          // Callback to refresh parent data
          if (onPhotoDeleted) {
            await onPhotoDeleted();
          }

          setDeleting(false);
          setDeleteConfirmOpen(false);
          setPhotoToDelete(null);
        }
        else {
          const idToken = await user.getIdToken();
          const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/unassign/${photoToDelete}`;
          const response = await axios.put(apiUrl, {
            projectCode: projectCode,
            title: title,
            option: option,
            photoId: photoToDelete,
          }, {
            headers: { 'Authorization': `Bearer ${idToken}` },
          });
          if (response.data) {
            console.log('Option unassigned successfully:', response.data);
            if (onPhotoDeleted) {
              await onPhotoDeleted();
            }
            setDeleteConfirmOpen(false);
            setPhotoToDelete(null);
            setDeleting(false);
          } else {
            throw new Error(t('errors.processing.unassignOptionFailed'));
          }
        }
      } catch (err) {
        captureException(err, { tags: { feature: 'photoGallery' }, extra: { action: 'deletePhoto', photoId: photoToDelete, project_code: projectCode } });
        console.error('Error deleting photo:', err);
        let errorMessage = t('gallery.deleteError');
        if (axios.isAxiosError(err) && err.response) {
          errorMessage = `Error: ${err.response.data?.details || err.response.data?.error || err.message}`;
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        setDeleteError(errorMessage);
        setDeleting(false);
      }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setPhotoToDelete(null);
    setDeleteError(null);
    setDeleting(false);
  };

  if (!open || (images?.length || 0) === 0) return null;

  const currentImage = images?.[currentIndex];

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          color: 'white',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            p: 2,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
          }}
        >
          {/* Bounding Box Toggle Button */}
          {currentImage?.bboxes && currentImage?.bboxes?.length > 0 ? (
            <IconButton 
              onClick={() => setShowBoundingBoxes(!showBoundingBoxes)} 
              sx={{ color: 'white', mr: "auto" }} 
              size="small"
              title={showBoundingBoxes ? t('gallery.hideBoundingBoxes') : t('gallery.showBoundingBoxes')}
            >
              {showBoundingBoxes ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          ): (
            null
          )}
          {enableAddPhoto && project?.editable && (
            <IconButton 
              onClick={handleAddPhotoClick} 
              sx={{ color: 'white', mr: "auto", ml: currentImage?.bboxes && currentImage?.bboxes?.length > 0 ? "auto" : 0 }} 
              size="small"
              title={t('gallery.addPhoto')}
              disabled={processing || qualityCheck.open}
            >
              {processing || qualityCheck.open ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Add />}
            </IconButton>
          )}
          {/* Delete Button Overlay - Only show for current image when multiple is true */}
          {(multiple || !mandatory || isOption) && !grouped && project?.editable && (
              <IconButton
                onClick={(e) => handleDeleteClick(e, currentImage?.id || '')}
                sx={{
                  backgroundColor: 'rgba(211, 47, 47, 0.8)',
                  color: 'white',
                  mr: "auto",
                  ml: currentImage?.bboxes && currentImage?.bboxes?.length > 0 || enableAddPhoto ? "auto" : 0,
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.95)',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
                size="small"
                title={t('gallery.deletePhoto')}
              >
                <DeleteIcon />
              </IconButton>
          )}
          
          {/* Close Button */}
          <IconButton onClick={onClose} sx={{ color: 'white', ml: "auto" }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Main Image Container */}
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            touchAction: grouped ? 'pan-x pan-y pinch-zoom' : (zoom === 1 ? 'pan-y pinch-zoom' : 'pan-x pan-y pinch-zoom'),
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
          }}
          onTouchStart={grouped ? undefined : handleTouchStart}
          onTouchMove={grouped ? undefined : handleTouchMove}
          onTouchEnd={grouped ? undefined : handleTouchEnd}
        >
          {grouped ? (
            /* Grouped View - Layout based on aspect ratios */
            (() => {
              // Check if both images are horizontal (width > height, aspect ratio > 1)
              // Only stack vertically if we have exactly 2 images, both loaded, and both horizontal
              const aspectRatios = images?.map(img => imageAspectRatios.get(img.id)).filter(ratio => ratio !== undefined) || [];
              const allAspectRatiosLoaded = images && imageAspectRatios.size >= images.length && aspectRatios.length;
              const allLoaded = aspectRatios.length === images?.length && images?.length === 2;
              const bothHorizontal = allLoaded && aspectRatios.every(ratio => ratio !== undefined && ratio > 1);
              const flexDirection = bothHorizontal ? 'column' : 'row';
              const overflowX = bothHorizontal ? 'hidden' : 'auto';
              const overflowY = bothHorizontal ? 'auto' : 'hidden';
              
              return (
                <Box
                  sx={{
                    width: '100%',
                    height: '85%',
                    display: 'flex',
                    flexDirection: flexDirection,
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    overflowX: overflowX,
                    overflowY: overflowY,
                    px: 2,
                    py: 2,
                    position: 'relative',
                  }}
                >
                  {/* Loading indicator while aspect ratios are being computed */}
                  {!allAspectRatiosLoaded && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <CircularProgress sx={{ color: 'white' }} />
                      <Typography sx={{ color: 'white' }}>{t('gallery.loading')}</Typography>
                    </Box>
                  )}
              {images?.map((image: GalleryImage, index: number) => (
                <Box
                  key={image.id}
                  sx={{
                    flexShrink: 0,
                    width: bothHorizontal ? '100%' : `calc((100% - ${(images?.length || 1) - 1} * 16px) / ${images?.length || 1})`,
                    maxWidth: bothHorizontal ? '100%' : '50%',
                    height: bothHorizontal ? `calc((100% - ${(images?.length || 1) - 1} * 16px) / ${images?.length || 1})` : '100%',
                    maxHeight: bothHorizontal ? '50%' : '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                    }}
                  >
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                      {image.thumbnail_url && (<img
                        src={image.thumbnail_url}
                        alt={image.zone}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                      />)}
                      <img
                        ref={(el) => {
                          if (el) {
                            imageRefs.current.set(image.id, el);
                          } else {
                            imageRefs.current.delete(image.id);
                          }
                        }}
                        src={image.url}
                        alt={image.zone}
                        onLoad={(e) => handleImageLoad(image.id, e)}
                        onError={handleImageError}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 1,
                        }}
                        draggable={false}
                      />
                    </div>
                    
                    {/* Bounding Box Overlay for grouped view */}
                    {(() => {
                      const currentImageRef = imageRefs.current.get(image.id);
                      const shouldShowBoundingBoxes = showBoundingBoxes && 
                        image.bboxes && 
                        image.bboxes.length > 0 && 
                        loadedImages.has(image.id) && 
                        currentImageRef && 
                        currentImageRef.naturalWidth > 0 && 
                        currentImageRef.naturalHeight > 0;

                      if (!shouldShowBoundingBoxes) return null;

                      const naturalWidth = currentImageRef.naturalWidth;
                      const naturalHeight = currentImageRef.naturalHeight;
                      const containerWidth = currentImageRef.offsetWidth;
                      const containerHeight = currentImageRef.offsetHeight;
                      
                      const imageAspect = naturalWidth / naturalHeight;
                      const containerAspect = containerWidth / containerHeight;
                      
                      let displayedWidth, displayedHeight;
                      if (imageAspect > containerAspect) {
                        displayedWidth = containerWidth;
                        displayedHeight = containerWidth / imageAspect;
                      } else {
                        displayedHeight = containerHeight;
                        displayedWidth = containerHeight * imageAspect;
                      }
                      
                      const offsetX = (containerWidth - displayedWidth) / 2;
                      const offsetY = (containerHeight - displayedHeight) / 2;

                      return (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              width: displayedWidth,
                              height: displayedHeight,
                              left: offsetX,
                              top: offsetY,
                            }}
                          >
                            {image.bboxes?.map((bbox, bboxIndex) => (
                              <Box
                                key={bboxIndex}
                                sx={{
                                  position: 'absolute',
                                  left: `${bbox.x * 100}%`,
                                  top: `${bbox.y * 100}%`,
                                  width: `${bbox.width * 100}%`,
                                  height: `${bbox.height * 100}%`,
                                  border: '3px solid #00FF00',
                                  backgroundColor: 'rgba(255, 68, 68, 0.1)',
                                  borderRadius: '2px',
                                  boxSizing: 'border-box',
                                  zIndex: 2,
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              ))}
                </Box>
              );
            })()
          ) : (
            /* Carousel View - Single image at a time */
          <Box
            sx={{
              width: '100%',
              height: '85%',
              display: 'flex',
              alignItems: 'center',
              overflowX: 'hidden',
              overflowY: 'auto',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                // ------- FIX 2: preserve base offset while dragging -------
                transform:
                  zoom === 1
                    ? (isDragging
                        ? `translateX(calc(${-currentIndex * 100}% + ${dragOffset}px))`
                        : `translateX(${-currentIndex * 100}%)`)
                    : `translateX(${-currentIndex * 100}%)`,
                // ---------------------------------------------------------
                transition: `transform ${
                  transition && zoom === 1
                    ? '0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                    : '0.2s ease-in-out'
                }`,
                willChange: 'transform',
              }}
            >
              {images?.map((image: GalleryImage, index: number) => (
                <Box
                  key={image.id}
                  sx={{
                    width: '100%',
                    height: '100%',
                    margin: "auto",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      margin: "auto",
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: Math.abs(index - currentIndex) > 1 ? 0.3 : 1,
                      transform: `scale(${
                        index === currentIndex ? zoom : Math.max(0.8, zoom * 0.7)
                      })`,
                      transition: `opacity 0.3s ease, transform 0.3s ease`,
                      boxShadow:
                        index === currentIndex
                          ? '0 10px 30px rgba(0,0,0,0.5)'
                          : '0 5px 15px rgba(0,0,0,0.3)',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                      '.delete-button-overlay': {
                        opacity: 1,
                      },
                    }}
                  >
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                      {image.thumbnail_url && (<img
                        src={image.thumbnail_url}
                        alt={image.zone}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          position: 'absolute',
                          top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                        }}
                      />)}
                      <img
                        ref={(el) => {
                          if (el) {
                            imageRefs.current.set(image.id, el);
                          } else {
                            imageRefs.current.delete(image.id);
                          }
                        }}
                        src={image.url}
                        alt={image.zone}
                        onLoad={() => handleImageLoad(image.id)}
                        onError={handleImageError}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          position: 'absolute',
                          cursor: zoom > 1 ? 'grab' : 'pointer',
                          top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                          zIndex: 1,
                        }}
                        draggable={false}
                      />
                    </div>
                    
                    {/* Bounding Box Overlay */}
                    {(() => {
                      const currentImageRef = imageRefs.current.get(image.id);
                      const shouldShowBoundingBoxes = showBoundingBoxes && 
                        image.bboxes && 
                        image.bboxes.length > 0 && 
                        index === currentIndex && 
                        loadedImages.has(image.id) && 
                        currentImageRef && 
                        currentImageRef.naturalWidth > 0 && 
                        currentImageRef.naturalHeight > 0;

                      if (!shouldShowBoundingBoxes) return null;

                      // Calculate actual displayed image size (accounting for objectFit: contain)
                      const naturalWidth = currentImageRef.naturalWidth;
                      const naturalHeight = currentImageRef.naturalHeight;
                      const containerWidth = currentImageRef.offsetWidth;
                      const containerHeight = currentImageRef.offsetHeight;
                      
                      // Calculate aspect ratios
                      const imageAspect = naturalWidth / naturalHeight;
                      const containerAspect = containerWidth / containerHeight;
                      
                      // Calculate displayed dimensions after objectFit: contain
                      let displayedWidth, displayedHeight;
                      if (imageAspect > containerAspect) {
                        // Image is wider than container - fit to width
                        displayedWidth = containerWidth;
                        displayedHeight = containerWidth / imageAspect;
                      } else {
                        // Image is taller than container - fit to height
                        displayedHeight = containerHeight;
                        displayedWidth = containerHeight * imageAspect;
                      }
                      
                      // Calculate offset to center the image
                      const offsetX = (containerWidth - displayedWidth) / 2;
                      const offsetY = (containerHeight - displayedHeight) / 2;

                      return (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              width: displayedWidth,
                              height: displayedHeight,
                              left: offsetX,
                              top: offsetY,
                            }}
                          >
                            {image.bboxes?.map((bbox, bboxIndex) => (
                              <Box
                                key={bboxIndex}
                                sx={{
                                  position: 'absolute',
                                  left: `${bbox.x * 100}%`,
                                  top: `${bbox.y * 100}%`,
                                  width: `${bbox.width * 100}%`,
                                  height: `${bbox.height * 100}%`,
                                  border: '3px solid #00FF00',
                                  backgroundColor: 'rgba(255, 68, 68, 0.1)',
                                  borderRadius: '2px',
                                  boxSizing: 'border-box',
                                  zIndex: 2,
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          )}

          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
              }}
            >
              <Typography>{t('gallery.loading')}</Typography>
            </Box>
          )}
        </Box>

        {/* Thumbnail Navigation */}
        {(images?.length || 0) > 1 && !isMobile && !grouped && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              overflowX: 'auto',
              zIndex: 10,
            }}
          >
            {images?.map((image: GalleryImage, index: number) => (
              <Box
                key={image.id}
                onClick={() => {
                  setCurrentIndex(index);
                  setZoom(1);
                  setDragOffset(0);
                  setIsDragging(false);
                }}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border:
                    currentIndex === index ? '2px solid white' : '2px solid transparent',
                  opacity: currentIndex === index ? 1 : 0.7,
                  transition: 'all 0.2s ease-in-out',
                  position: 'relative',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  {image.thumbnail_url && (<img
                    src={image.thumbnail_url}
                    alt={image.zone}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />)}
                  
                  {/* Delete Button on Thumbnail - Only show when multiple is true */}
                  {(multiple || !mandatory || isOption) && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(e, image?.id || '');
                      }}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 20,
                        height: 20,
                        minWidth: 20,
                        padding: 0,
                        backgroundColor: 'rgba(211, 47, 47, 0.9)',
                        color: 'white',
                        zIndex: 2,
                        opacity: 0,
                        transition: 'opacity 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 1)',
                          opacity: 1,
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: '14px',
                        },
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0';
                      }}
                      title={t('gallery.deletePhoto')}
                    >
                      <CloseIcon sx={{ fontSize: '14px' }} />
                    </IconButton>
                  )}
                </div>
              </Box>
            ))}
          </Box>
        )}

        {/* Mobile Navigation Dots */}
        {(images?.length || 0) > 1 && isMobile && !grouped && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '90vw',
              maxWidth: '90%',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 0.5,
              zIndex: 10,
            }}
          >
            {images?.map((_: GalleryImage, index: number) => (
              <Box
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                  setZoom(1);
                  setDragOffset(0);
                  setIsDragging(false);
                }}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor:
                    currentIndex === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      {/* Hidden File Input */}
      <input
        type="file"
        accept="image/*"
        hidden
        ref={cameraInputRef}
        onChange={handleFileSelected}
        onClick={(event) => event.stopPropagation()}
        capture="environment"
      />

      {/* Upload Error Alert */}
      {uploadError && (
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: '0px !important', 
            fontSize: '0.875rem', 
            zIndex: (theme) => theme.zIndex.drawer + 200, 
            position: 'sticky', 
            bottom: 0, 
            left: 0, 
            right: 0 
          }}
          action={
            <IconButton onClick={() => setUploadError(null)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          }
        >
          {uploadError}
        </Alert>
      )}

      {/* Quality Check Dialog */}
      <Dialog
        open={qualityCheck.open}
        onClose={handleQualityCheckClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('qualityCheck.title')}
        </DialogTitle>
        <DialogContent>
          {qualityCheck.report && (
            <Box sx={{ mb: 2 }}>
              <Alert 
                severity={qualityCheck.report.summary === 'bad' ? 'error' : 'warning'}
                sx={{ borderRadius: '0px !important', mb: 2 }}
              >
                <Typography variant="body2">
                  {qualityCheck.report.summary === 'bad'
                    ? t('qualityCheck.poorQuality', { score: qualityCheck.report.score })
                    : t('qualityCheck.acceptableQuality', { score: qualityCheck.report.score })
                  }
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleQualityCheckClose} color="primary">
            {t('qualityCheck.retakePhoto')}
          </Button>
          {qualityCheck.report && qualityCheck.report.summary !== 'bad' && (
            <Button onClick={handleProceedWithQuality} variant="contained" color="primary">
              {t('qualityCheck.useAnyway')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('gallery.deleteConfirm')}</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography variant="body2">
            {t('gallery.deleteConfirmMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? t('gallery.deleting') : t('gallery.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Options Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleCapturePhoto}>
          <PhotoCamera sx={{ mr: 1, color: 'primary.main' }} />
          {t('options.capturePhoto')}
        </MenuItem>
        {project && (
          <MenuItem onClick={handleSelectGallery}>
            <Collections sx={{ mr: 1, color: 'primary.main' }} />
            {t('options.selectGallery')}
          </MenuItem>
        )}
      </Menu>

      {/* Photo Selector Gallery */}
      {project && (
        <PhotoSelectorGallery
          open={selectorOpen}
          onClose={() => setSelectorOpen(false)}
          onSelectPhoto={handlePhotoSelected}
          project={project}
          currentZone={option || ''}
          isMobile={isMobile}
        />
      )}
    </Dialog>
  );
};

export default PhotoGallery;